import { get } from 'lodash'
import { runInAction } from 'mobx'
import rootStore from '../..'
import { wrapWithLoading } from '../../../config/general-actions'
import { responseHasError } from '../../users/actions'
import { PLAN_FOR_DELETION_NR_OF_DAYS, warnPt } from '../../../config'
import { requireRecruitmentById } from '../../recruitment/actions'
import dayjs from 'dayjs'
import { deleteLstItem } from '../../../api/utils/api-delete'
import { getCompactLst } from '../../../api/utils/api-compact'
const _storeKey = 'uiLstRecruitment'

/**
 * @async
 * @function actLoadRecruitments
 * @description Asynchronously loads recruitment list from the server and updates the UI list store.
 * @returns {Promise<void>}
 */
export const actLoadRecruitments = wrapWithLoading(
	async () => {
		const params = {
			pg: rootStore[_storeKey].pg,
			rpg: rootStore[_storeKey].rpg,
			progress: rootStore[_storeKey].filter?.progress || ''
		}
		const _response = await getCompactLst('recruitments', params)

		const hasError = responseHasError(_response)
		if (!hasError && _response.status === 'success') {
			runInAction(() => {
				_actSync(
					get(_response, ['itms'], []),
					get(_response, ['nr'], 0)
				)
			})
		} else {
			throw _response
		}
	},
	{ loadingMessage: 'Loading Recruitment' }
)

/**
 * @function _actSync
 * @description Syncs the server items with the store items.
 * @param {Array} srvItms - Server items.
 * @param {number} nr - Number of items.
 */
const _actSync = (srvItms, nr) => {
	warnPt(srvItms, nr)
	runInAction(() => {
		for (const _srvItm of srvItms) {
			const _storeItm = requireRecruitmentById(_srvItm.recruitment_id)
			_storeItm.updateFromServer(_srvItm)
		}
		rootStore[_storeKey].setIds(srvItms.map((it) => it.recruitment_id))
		rootStore[_storeKey].setNr(nr)
	})
}

/**
 * @function actUndoOrDeleteRecruitment
 * @description Marks a recruitment item for deletion or undoes the deletion flag.
 * If in demo mode, updates the store without hitting the backend.
 * Otherwise, sends a delete request and updates the item state accordingly.
 *
 * @param {string} recruitmentId - ID of the recruitment item to delete/undo.
 * @param {boolean} [status=true] - Whether to delete (true) or undo delete (false).
 * @param {boolean} [demo=false] - Whether this operation is a demo (mock) action.
 * @returns {Promise<boolean|void>} Resolves true in demo mode or void after server sync.
 */
export const actUndoOrDeleteRecruitment = wrapWithLoading(
	async (recruitmentId, status = true, demo = false) => {
		if (demo) {
			runInAction(() => {
				rootStore[_storeKey].setIds(
					rootStore[_storeKey].ids.filter(
						(id) => id !== recruitmentId
					)
				)
				rootStore[_storeKey].setNr(rootStore[_storeKey].nr - 1)
			})

			return true
		}

		const plan_for_deletion = dayjs()
			.add(PLAN_FOR_DELETION_NR_OF_DAYS['recruitment'], 'day')
			.unix()
		const timestamp = status ? plan_for_deletion : false

		const _response = await deleteLstItem('recruitment', {
			id: recruitmentId,
			to_delete: timestamp || false
		})
		const _storeItm = requireRecruitmentById(recruitmentId)
		_storeItm.setToDelete(timestamp)

		const hasError = responseHasError(_response)
		if (!hasError && _response.status === 'success') {
			runInAction(() => {
				_storeItm.setToDelete(timestamp || false)

				rootStore[_storeKey].setIds(
					rootStore[_storeKey].ids.filter(
						(id) => id !== recruitmentId
					)
				)
				rootStore[_storeKey].setNr(rootStore[_storeKey].nr - 1)
			})
		} else {
			throw _response
		}
	},
	{ loadingMessage: 'Del/Undo Recruitment' }
)
